import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '../material';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
  CdkDrag,
  CdkDropList,
  copyArrayItem,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialogModule } from '@angular/material/dialog';
import { DialogService } from '../services/dialog.service';
import { TaskModel } from '../models/station.model';
import { ItemListPanelConstants } from '../models/constants/item-list-panel.constants';
import { v4 as uuidv4 } from 'uuid';
import { QBuiltService } from 'src/app/modules/qbuilt/services/qbuilt-service';
import { MatTooltipModule } from '../material';
import { ActivatedRoute } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'item-list-panel',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    DragDropModule,
    CdkDrag,
    CdkDropList,
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatAutocompleteModule,
  ],
  templateUrl: './item-list-panel.component.html',
  styleUrl: './item-list-panel.component.scss',
})
export class ItemListPanelComponent {
  @Input() panelType: number;
  @Input() isEditMode: boolean = false;
  @Output() stationUpdateEvent = new EventEmitter<any>();
  @Output() taskItemUpdateEvent = new EventEmitter<any>();

  //initial task list arrays
  allTasks: any[] = [];
  requiredTasks: any[] = [];
  optionalTasks: any[] = [];

  //lists used within the panels
  stationList: any[] = [];
  taskList: any[] = [];
  itemList: any[] = [];

  departments: { value: string; viewValue: string }[] = [
    { value: 'All', viewValue: 'All' },
  ];
  deptSelected: string = '';
  deptControl = new FormControl('');
  unusedToggleStatus: boolean = false;
  cciCodes: { value: string; codeId: string; taskIds: string[] }[] = [
    { value: 'All', codeId: 'All', taskIds: ['All'] },
  ];
  cciCodeSelected: string = '';
  cciCodeControl = new FormControl('');
  originalCCICodes: { value: string; codeId: string; taskId: string }[] = [];
  filteredCciCodes: { value: string; codeId: string; taskIds: string[] }[] = [];

  //booleans to control filters being on or off
  cciFilterActive: boolean = false;
  deptFilterActive: boolean = false;

  form = new FormGroup({
    deptControl: new FormControl(''),
    cciCodeControl: new FormControl(''),
  });

  // Panel Variables
  panelTitle: string = '';
  deptDropdown: boolean = false;
  unusedToggle: boolean = false;
  unusedToggleText: string = 'Show Unused Items';
  searchBar: boolean = false;
  requiredWarning: boolean = false;
  warningTooltip: string = '';
  panelTooltip: string = '';
  setupTooltip: string = '';
  ogItemContainer: string = 'parentList';

  allRequired: boolean = false;

  //search bar variables
  searchText: string = '';
  searchActive: boolean = false;
  filteredList: any[] = [];

  hasConfiguration: boolean = false;
  classificationMap = new Map<number, any[]>();

  resetEnabled: boolean = false;

  constructor(
    private dialogService: DialogService,
    private qbuiltService: QBuiltService,
    public itemConstants: ItemListPanelConstants,
    public activatedRoute: ActivatedRoute,
  ) {
    this.qbuiltService.secondFormData$.subscribe((data) => {
      this.stationList = data || [];
    });

    this.allTasks = JSON.parse(localStorage.getItem('configuredTasks') || '[]');
    this.allRequired =
      sessionStorage.getItem('allRequired') === 'true' ? true : false;
    this.hasConfiguration =
      sessionStorage.getItem('hasConfiguration') === 'true' ? true : false;
  }

  ngOnInit() {
    this.getTasksByKind();

    this.form.get('cciCodeControl')?.valueChanges.subscribe((value) => {
      this.filteredCciCodes = this._filterCciCodes(value || '');
    });
    //assigns already existing station list to both panel windows
    this.stationList =
      JSON.parse(sessionStorage.getItem('secondFormGroup') || '[]')
        .stationList || [];

    if (this.stationList.length > 0) {
      this.trackStationItems(this.stationList);
      this.checkRequiredCount();
    }

    //check for configuration - if true disabled delete/creation of stations
    this.hasConfiguration =
      localStorage.getItem('hasConfiguration') === 'true' ? true : false;
  }

  /**
   * Sets up the initial data for required tasks and AOC optional tasks
   * @param tasks
   * @param panelType
   */
  setupData(tasks: any[], panelType: number) {
    //get required tasks and AOC optional tasks and put into separate arrays
    if (panelType === 1) {
      this.panelTitle = this.itemConstants.requiredPanelTitle;
      this.deptDropdown = true;
      this.unusedToggle = true;
      this.searchBar = true;
      this.requiredWarning = true;
      this.warningTooltip = this.itemConstants.requiredWarningTooltip;
      this.panelTooltip = this.itemConstants.emptyTextSidePanel;
      this.setupTooltip = this.itemConstants.emptyTextSetupPanel;
      this.itemList = this.getRequired(tasks);
      this.setupDepartmentData(panelType);
      this.setupCCICodes(this.itemList);
      this.checkRequiredCount();
    } else if (panelType === 2) {
      this.panelTitle = this.itemConstants.optionalPanelTitle;
      this.deptDropdown = false;
      this.unusedToggle = true;
      this.searchBar = true;
      this.requiredWarning = false;
      this.itemList = this.getOptionalTasks(tasks);
      this.optionalTasks = this.itemList;
      this.setupDepartmentData(panelType);
      this.setupCCICodes(this.itemList);
      this.qbuiltService.setCurrentItemList(this.itemList);
    }
  }

  getTasksByKind() {
    this.qbuiltService.GetAllTasksByKinds();
    this.qbuiltService.tasksByKindData$.subscribe((data) => {
      this.buildClassificationMap(data.Items);

      this.setupData(this.allTasks, this.panelType);
    });
  }

  buildClassificationMap(tasksByKind: any[]) {
    tasksByKind.forEach((t) => {
      this.classificationMap.set(
        Number(t.InspectionTaskId),
        t.ClassificationAssignments || [],
      );
    });
  }

  getOptionalTasks(tasks: any[]) {
    const optionalOnly = tasks.filter((t) => t.RequiredFlag === false);

    this.optionalTasks = optionalOnly.map((task) => {
      return {
        taskId: task.InspectionTaskId,
        taskType: this.itemConstants.optionalItem,
        taskName: task.InspectionTaskName,
        taskDepartments: task.InspectionTaskDepartments,
        classificationAssignments: task.ClassificationAssignments || [],
      };
    });

    const taskListItems = this.optionalTasks.map((t) => t.taskId);
    sessionStorage.setItem('optionalTaskIds', JSON.stringify(taskListItems));
    this.qbuiltService.setOptionalTasks(this.optionalTasks);
    return this.optionalTasks;
  }

  //setups all required tasks
  getRequired(tasks: any[]) {
    const requiredOnly = tasks.filter((t) => t.RequiredFlag === true);

    this.requiredTasks = requiredOnly.map((task) => {
      return {
        taskId: task.InspectionTaskId,
        taskType: this.itemConstants.requiredItem,
        taskName: task.InspectionTaskName,
        taskDepartments: task.InspectionTaskDepartments,
        classificationAssignments: task.ClassificationAssignments || [],
      };
    });

    const taskListItems = this.requiredTasks.map((t) => t.taskId);
    sessionStorage.setItem('requiredTaskIds', JSON.stringify(taskListItems));
    this.qbuiltService.setAllRequiredTasks(this.requiredTasks);

    return this.requiredTasks;
  }

  isOverflowing(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth;
  }

  getDisplayTaskName(task: any): string {
    // Always pull from classificationMap
    const assignments = this.classificationMap.get(Number(task.taskId)) || [];

    if (assignments.length === 0) {
      return task.taskName;
    }

    const subtypes = assignments
      .filter((ca) => ca.Classification?.Type?.toLowerCase() !== 'department')
      .map((ca) => ca.Classification.SubType);

    if (subtypes.length > 0) {
      return `<b>(${subtypes.join(', ')})</b> ${task.taskName}`;
    }

    return task.taskName;
  }

  //Checks against List containing used task ids then updates styling of item panel
  checkTaskInUse(taskId: string): boolean {
    let itemsInUse = JSON.parse(sessionStorage.getItem('stationItems') || '[]');
    return itemsInUse.includes(taskId);
  }

  // Gets all possible departments from required tasks for department dropdown values
  setupDepartmentData(panelType: number) {
    const sourceTasks =
      panelType === 1 ? this.requiredTasks : this.optionalTasks;

    sourceTasks.forEach((task) => {
      task.taskDepartments.forEach((department: any) => {
        const name = department.DepartmentName;

        if (!this.departments.some((d) => d.value === name)) {
          this.departments.push({
            value: name,
            viewValue: name,
          });
        }
      });
    });

    // Restore previously selected department if available
    const saved = sessionStorage.getItem('selectedDepartment');
    if (saved) {
      this.deptSelected = saved;
      this.deptControl.setValue(saved);
      this.filterTaskByDepartment(this.deptSelected);
    }
  }

  //setups CCI codes for dropdown values
  setupCCICodes(itemList: any[]) {
    itemList.forEach((task) => {
      const cciCodeAssignment = task.classificationAssignments.find(
        (ca: any) => ca.Classification?.SubType?.toLowerCase() === 'cci',
      );

      if (cciCodeAssignment) {
        const cciCode = cciCodeAssignment.Classification.Codes[0]; // Assuming the first code is the relevant one
        //add all items to temp cciList item
        if (!this.originalCCICodes.some((c) => c.codeId === cciCode.Id)) {
          this.originalCCICodes.push({
            value: cciCode.Code.split('.')[0], // Display only the part before the colon
            codeId: cciCode.Id,
            taskId: task.taskId,
          });
        }
      }
    });

    //filters out duplicates and appends all taskIds associated with each CCI code to be used for filtering later on
    this.cciCodes = [
      { value: 'All', codeId: 'All', taskIds: ['All'] },
      ...Object.values(
        this.originalCCICodes.reduce(
          (acc, code) => {
            if (acc[code.value]) {
              acc[code.value].taskIds.push(code.taskId);
            } else {
              acc[code.value] = {
                value: code.value,
                codeId: code.codeId,
                taskIds: [code.taskId],
              };
            }
            return acc;
          },
          {} as Record<
            string,
            { value: string; codeId: string; taskIds: string[] }
          >,
        ),
      ),
    ];

    this.filteredCciCodes = this.cciCodes;
  }

  // Unified filter method that applies both department + search text
  applyFilters() {
    const sourceTasks =
      this.panelType === 1 ? this.requiredTasks : this.optionalTasks;
    let filtered = [...sourceTasks];

    // Apply department filter
    if (this.deptSelected && this.deptSelected !== 'All') {
      this.resetEnabled = true;
      this.deptFilterActive = true;
      //disable cci code control so only one filter can be used at a time
      this.form.get('cciCodeControl')?.disable();
      filtered = filtered.filter((item: any) =>
        item.taskDepartments?.some(
          (dept: any) => dept.DepartmentName === this.deptSelected,
        ),
      );
    } else {
      this.deptFilterActive = false;
      this.form.get('cciCodeControl')?.enable();
    }

    //apply CCI code filter
    if (this.cciCodeSelected && this.cciCodeSelected !== 'All') {
      this.resetEnabled = true;
      this.cciFilterActive = true;
      //disable dept control so only one filter can be used at a time
      this.form.get('deptControl')?.disable();
      const selectedCode = this.cciCodes.find(
        (code) => code.value === this.cciCodeSelected,
      );
      filtered = filtered.filter((item: any) => {
        const itemCodeAssignment = item.classificationAssignments.find(
          (ca: any) => ca.Classification?.SubType?.toLowerCase() === 'cci',
        );
        return (
          itemCodeAssignment &&
          selectedCode?.taskIds.includes(item.taskId.toString())
        );
      });
    } else {
      this.cciFilterActive = false;
      this.form.get('deptControl')?.enable();
    }

    // Apply search text filter
    if (this.searchText?.trim()) {
      this.resetEnabled = true;
      const search = this.searchText.trim().toLowerCase();
      filtered = filtered.filter((item) =>
        item.taskName.toLowerCase().includes(search),
      );
    }

    this.itemList = filtered;
    this.searchActive = !!this.searchText?.trim();
    this.qbuiltService.setCurrentItemList(this.itemList);
  }

  // Handles changes to the department dropdown
  onDepartmentChange(event: any) {
    this.deptSelected = event.value;
    this.deptControl.setValue(event.value);
    sessionStorage.setItem('selectedDepartment', event.value);
    //will add logic here to call backend to retrieve item lists based on selected department
    this.applyFilters();
  }

  // Handles changes to the CCI code dropdown
  onCciCodeChange(event: MatAutocompleteSelectedEvent) {
    this.cciCodeSelected = event.option.value;
    //this.cciCodeControl.setValue(event.option.value);
    sessionStorage.setItem('selectedCciCode', event.option.value);
    //will add logic here to call backend to retrieve item lists based on selected CCI code
    this.applyFilters();
  }

  private _filterCciCodes(
    value: string,
  ): { value: string; codeId: string; taskIds: string[] }[] {
    const filterValue = value.toLowerCase();
    return this.cciCodes.filter((code) =>
      code.value.toLowerCase().includes(filterValue),
    );
  }
  /***
   * Captures user change to department filter
   * pulls only items containing the selected department and assigns
   * 'currentItemList' observable
   */
  filterTaskByDepartment(department: string) {
    this.deptSelected = department;
    this.applyFilters();
  }

  //resets all filters above drag and drop task items
  clearFilters() {
    this.resetEnabled = false;
    this.searchActive = false;
    this.searchText = '';
    this.deptSelected = '';
    this.form.get('deptControl')?.reset();
    this.cciCodeSelected = '';
    this.form.get('cciCodeControl')?.reset();
    sessionStorage.removeItem('selectedDepartment');
    sessionStorage.removeItem('selectedCciCode');
    this.applyFilters();
  }

  //Search Functionality
  onSearchInput() {
    this.applyFilters();
  }

  //clear search bar
  clearSearch() {
    this.resetEnabled = false;
    this.searchText = '';
    this.applyFilters();
  }

  // Handles the drag and drop event from task panel to station setup panel
  drop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      if (event.previousContainer.data[event.previousIndex]) {
        // Extract the dragged item
        const dragItem = event.item.data as TaskModel;

        // Create a new item with a unique ID
        const newItem = {
          taskId: dragItem.taskId,
          id: uuidv4(),
          taskName: dragItem.taskName,
          taskType: dragItem.taskType,
          taskDepartments: dragItem.taskDepartments,
          classificationAssignments: dragItem.classificationAssignments,
        };

        //dragging item from new station vs between stations to prevent duplicates
        if (event.previousContainer.id === this.ogItemContainer) {
          copyArrayItem(
            event.previousContainer.data,
            event.container.data,
            event.previousIndex,
            event.currentIndex,
          );

          event.container.data[event.currentIndex] = newItem;
        } else {
          event.container.data.splice(event.currentIndex, 0, newItem);
          event.previousContainer.data.splice(event.previousIndex, 1);
        }
      }

      this.trackStationItems(this.stationList);
      this.emitStationUpdateEvent(this.stationList);
    }
  }

  trackStationItems(stationList: any[]) {
    let stationItems: any[] = [];
    stationList.forEach((station) => {
      station.taskGroup.forEach((taskgroup: any) => {
        taskgroup.taskList.forEach((task: any) => {
          //only push unique taskIds to the array
          if (!stationItems.includes(task.taskId.toString())) {
            stationItems.push(task.taskId.toString());
          }
        });
      });
    });
    sessionStorage.setItem('stationItems', JSON.stringify(stationItems));
  }

  // Edit Station Functionality to trigger dialog
  editStation(group: any) {
    const dialogRef = this.dialogService.openStationDialog(
      this.itemConstants.dialogEditTitle,
      this.itemConstants.dialogInputTitle,
      this.itemConstants.dialogInputPlaceholder,
      group.name,
      this.itemConstants.dialogCancelButtonText,
      this.itemConstants.dialogEditConfirmButtonText,
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined || result === '') {
        //user clicked cancel - do nothing
      } else {
        //update the name here
        const stationIndex = this.stationList.findIndex(
          (station) => station.id === group.id,
        );
        if (stationIndex !== -1) {
          this.stationList[stationIndex].name = result;
        }
        this.emitStationUpdateEvent(this.stationList);
      }
    });
  }

  // Delete Station Functionality
  deleteStation(group: any) {
    this.stationList = this.stationList.filter(
      (station) => station.id !== group.id,
    );
    this.emitStationUpdateEvent(this.stationList);
  }

  // Add New Station Functionality and triggers dialog to name new Station
  addStation() {
    const dialogRef = this.dialogService.openStationDialog(
      this.itemConstants.dialogAddTitle,
      this.itemConstants.dialogInputTitle,
      this.itemConstants.dialogInputPlaceholder,
      this.itemConstants.dialogInputValue,
      this.itemConstants.dialogCancelButtonText,
      this.itemConstants.dialogAddConfirmButtonText,
    );

    //get response when user confirms and reset the form
    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined || result === '') {
        //user clicked cancel - do nothing
      } else {
        this.stationList.push({
          id: (this.stationList.length + 1).toString(),
          name: result,
          order: this.stationList.length + 1,
          taskGroup: [
            {
              id: (this.stationList.length + 1).toString(),
              name: result,
              taskList: ([] = []),
            },
          ],
        });

        this.emitStationUpdateEvent(this.stationList);
      }
    });
  }

  // deleting task from Station setup panel
  deleteTask(stationPassed: any, task: any) {
    const stationIndex = this.stationList.findIndex(
      (station) => station.id === stationPassed.id,
    );
    if (stationIndex !== -1) {
      this.stationList[stationIndex].taskGroup = this.stationList[
        stationIndex
      ].taskGroup.filter((taskGroup: any) => {
        taskGroup.taskList = taskGroup.taskList.filter(
          (t: any) => t.id !== task.id,
        );
        return taskGroup;
      });

      this.trackStationItems(this.stationList);
      this.emitStationUpdateEvent(this.stationList);
    }
  }

  /**
   * Emits changes to the Required Items form
   * to the parent component
   * @param data
   */
  emitStationUpdateEvent(data: any) {
    this.qbuiltService.updateSecondFormData(data);
    if (this.panelType == 1) {
      this.checkRequiredCount();
    }

    this.stationUpdateEvent.emit(data);
  }

  //disabled 'delete station' button if station contains tasks
  checkEmptyStation(station: any) {
    return (
      !station.taskGroup?.length || station.taskGroup[0].taskList.length === 0
    );
  }

  checkRequiredCount() {
    //setup empty arrays for taskIds
    const statListItems: any[] = sessionStorage.getItem('stationItems')
      ? JSON.parse(sessionStorage.getItem('stationItems') || '[]')
      : [];
    const taskListItems: any[] = sessionStorage.getItem('requiredTaskIds')
      ? JSON.parse(sessionStorage.getItem('requiredTaskIds') || '[]')
      : [];

    let setA = new Set(taskListItems.map((id: any) => id.toString()));
    let setB = new Set(statListItems.map((id: any) => id.toString()));

    if ([...setA].every((value) => setB.has(value))) {
      this.allRequired = true;
      sessionStorage.setItem('allRequired', 'true');
      this.qbuiltService.setAllRequiredItemsStatus(true);
    } else {
      this.allRequired = false;
      sessionStorage.setItem('allRequired', 'false');
      this.qbuiltService.setAllRequiredItemsStatus(false);
    }
  }

  ngOnDestroy() {
    sessionStorage.removeItem('selectedDepartment');
    sessionStorage.removeItem('stationItems');
    sessionStorage.removeItem('requiredTaskIds');
    sessionStorage.removeItem('optionalTaskIds');
  }
}
